/**
 * Azure DevOps Integration API
 *
 * API endpoints for managing Azure DevOps OAuth connections and credentials
 */

import fetchWithAuth from "@/api/secureFetch";
import type { ContextMeshRefreshNotice } from "./links";

// Jira/ADO endpoints now live in app/backend (ported from the retired agent service)
const AGENT_BASE_URL = import.meta.env.VITE_API_BASE_URL || "";

export interface AzureDevOpsCredentialsCheckResponse {
    hasCredentials: boolean;
    isConnected?: boolean;
    isActive?: boolean;
    isExpired?: boolean;
    organization?: string;
    projectName?: string;
    metadata?: {
        displayName?: string;
        email?: string;
        organization?: string;
        projectId?: string;
        projectName?: string;
    };
}

export interface InitiateOAuthRequest {
    userId: number;
    organization?: string;
}

export interface InitiateOAuthResponse {
    authUrl?: string;
    authorizationUrl?: string;
    state: string;
}

export interface OAuthCallbackParams {
    code: string;
    state: string;
}

export interface OAuthCallbackResponse {
    success: boolean;
    message?: string;
    error?: string;
}

export interface RefreshTokenResponse {
    success: boolean;
    message?: string;
    error?: string;
}

export interface DeleteCredentialsResponse extends ContextMeshRefreshNotice {
    success: boolean;
    message: string;
}

// Azure DevOps Sync Endpoint Types
export type AzureDevOpsInfoStatus = "NOT_LINKED" | "NOT_ACTIVE" | "LINKED";

export interface AzureDevOpsSyncRequest {
    userId: number;
}

export interface AzureDevOpsSyncResponse extends ContextMeshRefreshNotice {
    status: AzureDevOpsInfoStatus;
    message?: string;
    success?: boolean; // Present when status is "LINKED"
    deleted?: number; // Number of records deleted
    imported?: number; // Number of records imported
    error?: string; // Present when success is false
}

/**
 * Check if user has existing Azure DevOps credentials
 */
export async function checkAzureDevOpsCredentials(
    userId: number,
    token: string
): Promise<AzureDevOpsCredentialsCheckResponse> {
    const response = await fetchWithAuth(
        `${AGENT_BASE_URL}/api/v1/integrations/status?userId=${userId}&vendor=azure`,
        token,
        {
            method: "GET"
        }
    );

    if (!response.ok) {
        throw new Error(`Failed to check credentials: ${response.status}`);
    }

    return response.json();
}

/**
 * Initiate OAuth flow for Azure DevOps
 */
export async function initiateAzureDevOpsOAuth(
    request: InitiateOAuthRequest,
    token: string
): Promise<InitiateOAuthResponse> {
    const response = await fetchWithAuth(
        `${AGENT_BASE_URL}/api/v1/azure-devops/oauth/authorize`,
        token,
        {
            method: "POST",
            headers: {
                "Content-Type": "application/json"
            },
            body: JSON.stringify(request)
        }
    );

    if (!response.ok) {
        throw new Error(`Failed to initiate OAuth: ${response.status}`);
    }

    return response.json();
}

/**
 * Handle OAuth callback
 */
export async function handleAzureDevOpsOAuthCallback(
    params: OAuthCallbackParams,
    token: string
): Promise<OAuthCallbackResponse> {
    const response = await fetchWithAuth(
        `${AGENT_BASE_URL}/api/v1/azure-devops/oauth/callback?code=${params.code}&state=${params.state}`,
        token,
        {
            method: "GET"
        }
    );

    if (!response.ok) {
        throw new Error(`OAuth callback failed: ${response.status}`);
    }

    return response.json();
}

/**
 * Refresh an expired Azure DevOps OAuth token
 * Uses the stored refresh_token to obtain a new access_token
 */
export async function refreshAzureDevOpsToken(
    userId: number,
    token: string
): Promise<RefreshTokenResponse> {
    const response = await fetchWithAuth(
        `${AGENT_BASE_URL}/api/v1/azure-devops/oauth/refresh`,
        token,
        {
            method: "POST",
            headers: {
                "Content-Type": "application/json"
            },
            body: JSON.stringify({ userId })
        }
    );

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Failed to refresh token: ${response.status}`);
    }

    return response.json();
}

/**
 * Sync Azure DevOps data for a user
 *
 * Fetches all work items from the user's linked Azure DevOps project and syncs them to the database.
 * Uses the project stored in the user's credential config.
 *
 * Response statuses:
 * - NOT_LINKED: No credential exists for the user
 * - NOT_ACTIVE: Credential exists but is deactivated
 * - LINKED with success=true: Sync completed successfully
 * - LINKED with success=false: Azure DevOps API failed (existing data preserved)
 */
export async function syncAzureDevOpsData(
    request: AzureDevOpsSyncRequest,
    token: string
): Promise<AzureDevOpsSyncResponse> {
    const response = await fetchWithAuth(
        `${AGENT_BASE_URL}/api/v1/azure-devops/sync`,
        token,
        {
            method: "POST",
            headers: {
                "Content-Type": "application/json"
            },
            body: JSON.stringify(request)
        }
    );

    if (!response.ok) {
        throw new Error(`Failed to sync Azure DevOps data: ${response.status}`);
    }

    return response.json();
}

/**
 * Disconnect the current user's Azure DevOps integration.
 */
export async function deleteAzureDevOpsCredentials(token: string): Promise<DeleteCredentialsResponse> {
    const response = await fetchWithAuth(
        `${AGENT_BASE_URL}/api/v1/azure-devops/credentials/delete`,
        token,
        {
            method: "DELETE"
        }
    );

    if (!response.ok) {
        throw new Error(`Failed to delete Azure DevOps credentials: ${response.status}`);
    }

    return response.json();
}

export async function listAzureDevOpsProjects(
    organization: string,
    token: string
): Promise<{ projects: Array<{ id: string; name: string; description?: string }> }> {
    const response = await fetchWithAuth(`${AGENT_BASE_URL}/api/v1/azure-devops/projects`, token, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ organization })
    });
    if (!response.ok) {
        throw new Error(`Failed to list Azure DevOps projects: ${response.status}`);
    }
    return response.json();
}

export interface AdoImportProgress { stage: string; current: number; total: number; message?: string }
export interface AdoImportResult extends ContextMeshRefreshNotice { imported: number; failed: number; process_job_id?: string }

export async function importWorkItemsStream(
    req: { organization: string; projectId: string; projectName: string },
    token: string,
    onProgress: (p: AdoImportProgress) => void,
    signal?: AbortSignal
): Promise<AdoImportResult> {
    const response = await fetchWithAuth(`${AGENT_BASE_URL}/api/v1/azure-devops/import-workitems-stream`, token, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(req),
        signal
    });
    if (!response.ok || !response.body) {
        throw new Error(`Failed to start work item import: ${response.status}`);
    }
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";

    // SSE frames are `event: <type>\ndata: <json>\n\n`; chunks may split frames.
    while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        let boundary = buffer.indexOf("\n\n");
        while (boundary !== -1) {
            const frame = buffer.slice(0, boundary);
            buffer = buffer.slice(boundary + 2);
            boundary = buffer.indexOf("\n\n");

            const eventMatch = frame.match(/^event: (.+)$/m);
            const dataMatch = frame.match(/^data: (.+)$/m);
            if (!eventMatch || !dataMatch) continue;

            let payload: Record<string, unknown>;
            try {
                payload = JSON.parse(dataMatch[1]);
            } catch {
                continue;
            }

            if (eventMatch[1] === "error") {
                throw new Error(typeof payload.message === "string" ? payload.message : "Import failed");
            }
            if (eventMatch[1] === "complete") {
                const refresh = payload.context_mesh_refresh;
                const result: AdoImportResult = {
                    imported: typeof payload.imported === "number" ? payload.imported : 0,
                    failed: typeof payload.failed === "number" ? payload.failed : 0,
                    process_job_id: typeof payload.process_job_id === "string" ? payload.process_job_id : undefined
                };
                if (refresh && typeof refresh === "object") {
                    result.context_mesh_refresh = refresh as ContextMeshRefreshNotice["context_mesh_refresh"];
                }
                if (typeof payload.warning === "string") result.warning = payload.warning;
                return result;
            }
            if (eventMatch[1] === "progress") {
                onProgress({
                    stage: String(payload.stage ?? ""),
                    current: typeof payload.current === "number" ? payload.current : 0,
                    total: typeof payload.total === "number" ? payload.total : 0,
                    message: typeof payload.message === "string" ? payload.message : undefined
                });
            }
        }
    }
    throw new Error("Import stream ended unexpectedly");
}
